import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { LocationOn } from '@mui/icons-material';
import { get } from 'lodash';
import useGetAvatar from '@/hooks/useGetAvatar';
import type { ViewportItem } from '@/hooks/useGetAvatarsViewport';
import { composeUri } from '@/utils/compose';
import { AVATARS } from '@/utils/CONST';

function siteSubtitleFromViewportItem(item: ViewportItem): string | undefined {
    const p = item.parent;
    if (!p || typeof p === 'string') return undefined;
    const line = [get(p, 'addressText'), get(p, 'postalCode'), get(p, 'city')].filter(Boolean).join(' ');
    return line || get(p, 'name');
}

function StackItemRow({ item, onPick }: { item: ViewportItem; onPick: (id: string) => void }) {
    const avatarUriId = composeUri(AVATARS, item.id);
    const { avatar, isLoading } = useGetAvatar({ avatarUriId });
    const siteLine = siteSubtitleFromViewportItem(item);

    return (
        <ListItem disablePadding>
            <ListItemButton onClick={() => onPick(item.id)}>
                <ListItemAvatar>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'error.main' }}>
                        <LocationOn sx={{ color: 'common.white', fontSize: 22 }} />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={isLoading ? '…' : (avatar?.name?.trim() || '—')}
                    {...(siteLine
                        ? {
                              secondary: siteLine,
                              secondaryTypographyProps: { variant: 'caption' as const, noWrap: true },
                          }
                        : {})}
                />
            </ListItemButton>
        </ListItem>
    );
}

interface Props {
    open: boolean;
    anchorEl: HTMLElement | null;
    items: ViewportItem[];
    title: string;
    onClose: () => void;
    onSelectItem: (id: string) => void;
}

const MapStackItemsPopover = ({ open, anchorEl, items, title, onClose, onSelectItem }: Props) => {
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            slotProps={{
                paper: {
                    elevation: 4,
                    sx: { minWidth: 260, maxWidth: 360, overflow: 'hidden' },
                },
            }}
        >
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                    {title}
                </Typography>
            </Box>
            <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto', pb: 1 }}>
                {items.map((item) => (
                    <StackItemRow
                        key={item.id}
                        item={item}
                        onPick={(id) => {
                            onClose();
                            onSelectItem(id);
                        }}
                    />
                ))}
            </List>
        </Popover>
    );
};

export default MapStackItemsPopover;
